import { useContext, useEffect, useState } from "react";
import { ClassListType } from "utils/interfaces";
import { motion } from "framer-motion";
import { MainContext } from "components/utils/Contexts";
import { usePlatform } from "components/utils/PlatformContext";
import { singlify } from "utils/utils";
import {
  defaultBgVariants,
  defaultPanelVariants,
} from "../common/defaultValues";
import PopUpHeader from "../common/PopUpHeader";
import SelectSchoolPanel from "./SelectSchoolList";
import ClassesCardsList from "../common/ClassesCardsList";

interface AssignClassToCoursePanelProps {
  selectedClasses: ClassListType[];
  selectedLesson: string;
  lessonIdToAssign: string;
  setShowPopUp: React.Dispatch<React.SetStateAction<boolean>>;
  afterAssignFunction: () => void;
}

/**
 * Element that represent the pop up panel for adding a course inside a specific class.
 * @param selectedClasses List of selected classes (based on external decisions. i.e: selected to be assigned
 *                        for lessons, for organisations, for students, etc.).
 * @param selectedLesson Indicates to what type of lesson the classes should be assigned when pressing the
 *                       assign lesson button on lesson card in the customers admin/lessons view. If it's
 *                       undefined means that is part of a form.
 * @param lessonIdToAssign _id of the lesson to assign based on the selection of the `selectedLesson` parameter.
 * @param setShowPopUp Function used to define if the pop should be showed or closed.
 * @returns
 */
const AssignClassToCoursePanel: React.FC<AssignClassToCoursePanelProps> = ({
  selectedClasses,
  selectedLesson,
  lessonIdToAssign,
  setShowPopUp,
  afterAssignFunction,
}) => {
  // Get the context parameters
  const { classesTerminology, schoolTerminology } = usePlatform();
  const { userRole } = useContext(MainContext);

  // States definition
  const [popUpState, setPopUpState] = useState<"main" | "select-school">(
    userRole === "master admin" ? "select-school" : "main"
  );

  /**
   * Handle clicking in the black screen
   */
  const handleClickBlackScreen = () => {
    // Close the pop up
    setShowPopUp(false);
  };

  /**
   * Definition of the function when pressed back button
   */
  const handlePressBack = () => {
    // Check the state of main for master admins
    if (popUpState === "main" && userRole === "master admin") {
      // Come back to the select school options
      setPopUpState("select-school");
    }
    // Otherwise, close the pop up
    else setShowPopUp(false);
  };

  /**
   * Adding a listener for keyboard shortcuts
   */
  useEffect(() => {
    // Function to use on escape
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") handlePressBack();
    };

    // Adding the lister
    document.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [popUpState]);

  return (
    <>
      <motion.div
        className="fixed inset-0 w-full h-full bg-neutral-500 bg-opacity-40 backdrop-blur-[2px] z-20 flex flex-col flex-grow"
        variants={defaultBgVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
        onClick={handleClickBlackScreen}
        onAnimationComplete={(definition) => {
          if (definition === "exit") afterAssignFunction();
        }}
      />
      <motion.div
        className="fixed w-[462px] h-fit max-h-[90%] left-0 right-0 ml-auto mr-auto px-6 pt-6 pb-8 bottom-5 bg-white rounded-3xl flex-col justify-start items-start gap-6 inline-flex z-30"
        variants={defaultPanelVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.1, ease: "easeOut" }}
      >
        <div className="relative w-full h-fit max-h-[80vh] flex-col justify-start items-start gap-6 inline-flex">
          <PopUpHeader
            title={
              popUpState === "select-school"
                ? `Select ${singlify(schoolTerminology).toLowerCase()}`
                : `Assign to ${singlify(classesTerminology)}`
            }
            handlePressBack={handlePressBack}
          />
          {popUpState === "main" && (
            <ClassesCardsList
              selectedClasses={selectedClasses}
              closeOnceClickOnCard={false}
              selectedLesson={selectedLesson}
              lessonIdToAssign={lessonIdToAssign}
              setShowPopUp={setShowPopUp}
              setSelectedClasses={() => {}}
              showDoneButton={true}
            />
          )}
          {popUpState === "select-school" && (
            <SelectSchoolPanel setPopUpState={setPopUpState} />
          )}
        </div>
      </motion.div>
    </>
  );
};

export default AssignClassToCoursePanel;
